import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyCustomerKey,
  ShopifyCustomerPaymentMethodKey,
  ShopifyOrderKey,
  ShopifyOrderToSubscriptionContractKey,
  ShopifyShopKey,
  ShopifySubscriptionBillingAttemptKey,
  ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
  ShopifySubscriptionContractKey,
  ShopifySubscriptionContractToCustomerKey,
  ShopifySubscriptionContractToCustomerPaymentMethodKey,
  ShopifySubscriptionContractToShopKey,
  ShopifySubscriptionLineKey,
  ShopifySubscriptionLineToSubscriptionContractKey,
  ShopifySubscriptionManualDiscountKey,
  ShopifySubscriptionManualDiscountToSubscriptionContractKey,
} from "./keys";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";
import { ShopifyApiVersion } from "./ShopifyApiVersion";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySubscriptionContractKey,
  "2022-01": {
    name: "Shopify Subscription Contract",
    fields: [
      {
        name: "App",
        fieldType: FieldType.JSON, // TODO: Convert to BelongsTo once App is modelled
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "app", graphQLSelection: "id" } },
      },
      {
        name: "App Admin URL",
        fieldType: FieldType.URL,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "appAdminUrl",
            shopifyFieldKey: "__gadget_graphql_subscription_contract_app_admin_url",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Billing Attempts",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "billingAttempts" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionBillingAttemptKey,
          inverseFieldKey: ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
        },
      },
      {
        name: "Billing Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "billingPolicy",
            graphQLSelection: `
        anchors {
          day
          month
          type
        }
        interval
        intervalCount
        maxCycles
        minCycles
        `,
            shopifyFieldKey: "billing_policy",
          },
        },
      },
      {
        name: "Currency Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "currencyCode", shopifyFieldKey: "currency_code" } },
        configuration: {
          allowMultiple: false,
          options: [
            { name: "AED" },
            { name: "AFN" },
            { name: "ALL" },
            { name: "AMD" },
            { name: "ANG" },
            { name: "AOA" },
            { name: "ARS" },
            { name: "AUD" },
            { name: "AWG" },
            { name: "AZN" },
            { name: "BAM" },
            { name: "BBD" },
            { name: "BDT" },
            { name: "BGN" },
            { name: "BHD" },
            { name: "BIF" },
            { name: "BMD" },
            { name: "BND" },
            { name: "BOB" },
            { name: "BRL" },
            { name: "BSD" },
            { name: "BTN" },
            { name: "BWP" },
            { name: "BYN" },
            { name: "BYR" },
            { name: "BZD" },
            { name: "CAD" },
            { name: "CDF" },
            { name: "CHF" },
            { name: "CLP" },
            { name: "CNY" },
            { name: "COP" },
            { name: "CRC" },
            { name: "CVE" },
            { name: "CZK" },
            { name: "DJF" },
            { name: "DKK" },
            { name: "DOP" },
            { name: "DZD" },
            { name: "EGP" },
            { name: "ERN" },
            { name: "ETB" },
            { name: "EUR" },
            { name: "FJD" },
            { name: "FKP" },
            { name: "GBP" },
            { name: "GEL" },
            { name: "GHS" },
            { name: "GIP" },
            { name: "GMD" },
            { name: "GNF" },
            { name: "GTQ" },
            { name: "GYD" },
            { name: "HKD" },
            { name: "HNL" },
            { name: "HRK" },
            { name: "HTG" },
            { name: "HUF" },
            { name: "IDR" },
            { name: "ILS" },
            { name: "INR" },
            { name: "IQD" },
            { name: "IRR" },
            { name: "ISK" },
            { name: "JEP" },
            { name: "JMD" },
            { name: "JOD" },
            { name: "JPY" },
            { name: "KES" },
            { name: "KGS" },
            { name: "KHR" },
            { name: "KID" },
            { name: "KMF" },
            { name: "KRW" },
            { name: "KWD" },
            { name: "KYD" },
            { name: "KZT" },
            { name: "LAK" },
            { name: "LBP" },
            { name: "LKR" },
            { name: "LRD" },
            { name: "LSL" },
            { name: "LTL" },
            { name: "LVL" },
            { name: "LYD" },
            { name: "MAD" },
            { name: "MDL" },
            { name: "MGA" },
            { name: "MKD" },
            { name: "MMK" },
            { name: "MNT" },
            { name: "MOP" },
            { name: "MRU" },
            { name: "MUR" },
            { name: "MVR" },
            { name: "MWK" },
            { name: "MXN" },
            { name: "MYR" },
            { name: "MZN" },
            { name: "NAD" },
            { name: "NGN" },
            { name: "NIO" },
            { name: "NOK" },
            { name: "NPR" },
            { name: "NZD" },
            { name: "OMR" },
            { name: "PAB" },
            { name: "PEN" },
            { name: "PGK" },
            { name: "PHP" },
            { name: "PKR" },
            { name: "PLN" },
            { name: "PYG" },
            { name: "QAR" },
            { name: "RON" },
            { name: "RSD" },
            { name: "RUB" },
            { name: "RWF" },
            { name: "SAR" },
            { name: "SBD" },
            { name: "SCR" },
            { name: "SDG" },
            { name: "SEK" },
            { name: "SGD" },
            { name: "SHP" },
            { name: "SLL" },
            { name: "SOS" },
            { name: "SRD" },
            { name: "SSP" },
            { name: "STD" },
            { name: "SYP" },
            { name: "SZL" },
            { name: "THB" },
            { name: "TJS" },
            { name: "TMT" },
            { name: "TND" },
            { name: "TOP" },
            { name: "TRY" },
            { name: "TTD" },
            { name: "TWD" },
            { name: "TZS" },
            { name: "UAH" },
            { name: "UGX" },
            { name: "USD" },
            { name: "UYU" },
            { name: "UZS" },
            { name: "VEF" },
            { name: "VES" },
            { name: "VND" },
            { name: "VUV" },
            { name: "WST" },
            { name: "XAF" },
            { name: "XCD" },
            { name: "XOF" },
            { name: "XPF" },
            { name: "XXX" },
            { name: "YER" },
            { name: "ZAR" },
            { name: "ZMW" },
          ],
        },
      },
      {
        name: "Customer",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToCustomerKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "customer", graphQLSelection: "id", shopifyFieldKey: "customer_id" },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerKey,
        },
      },
      {
        name: "Customer Payment Method",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToCustomerPaymentMethodKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_customer_payment_method",
            graphQLFieldKey: "customerPaymentMethod",
            graphQLSelection: "id",
            graphQLOnlyField: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyCustomerPaymentMethodKey,
        },
      },
      {
        name: "Delivery Method",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_delivery_method",
            graphQLFieldKey: "deliveryMethod",
            graphQLSelection: `...on SubscriptionDeliveryMethodShipping {
          address {
            address1
            address2
            city
            company
            country
            countryCode
            firstName
            lastName
            name
            phone
            province
            provinceCode
            zip
          }
          shippingOption {
            carrierService {
              id
            }
            code
            description
            presentmentTitle
            title
          }
        }`,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Delivery Policy",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "deliveryPolicy",
            graphQLSelection: `anchors {
          day
          month
          type
        }
        interval
        intervalCount`,
            shopifyFieldKey: "delivery_policy",
          },
        },
      },
      {
        name: "Delivery Price",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_delivery_price",
            graphQLFieldKey: "deliveryPrice",
            graphQLSelection: `amount
currencyCode`,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Discounts",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "discounts" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionManualDiscountKey,
          inverseFieldKey: ShopifySubscriptionManualDiscountToSubscriptionContractKey,
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Last Payment Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_last_payment_status",
            graphQLFieldKey: "lastPaymentStatus",
            graphQLOnlyField: true,
          },
        },
        configuration: {
          allowMultiple: false,
          options: [{ name: "FAILED" }, { name: "SUCCEEDED" }],
        },
      },
      {
        name: "Lines",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "lines" } },
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifySubscriptionLineKey,
          inverseFieldKey: ShopifySubscriptionLineToSubscriptionContractKey,
        },
      },
      {
        name: "Next Billing Date",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_next_billing_date",
            graphQLFieldKey: "nextBillingDate",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Note",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_note",
            graphQLFieldKey: "note",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Orders",
        fieldType: FieldType.HasMany,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "orders" } },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
          inverseFieldKey: ShopifyOrderToSubscriptionContractKey,
        },
      },
      {
        name: "Origin Order",
        fieldType: FieldType.BelongsTo,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_origin_order",
            graphQLFieldKey: "originOrder",
            graphQLOnlyField: true,
            graphQLSelection: "id",
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_shopify_created_at",
            graphQLFieldKey: "createdAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_subscription_contract_shopify_updated_at",
            graphQLFieldKey: "updatedAt",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Status",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "status",
            shopifyFieldKey: "__gadget_graphql_subscription_contract_status",
            graphQLOnlyField: true,
          },
        },
        configuration: {
          allowMultiple: false,
          options: [{ name: "ACTIVE" }, { name: "CANCELLED" }, { name: "EXPIRED" }, { name: "FAILED" }, { name: "PAUSED" }],
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionContractToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "subscriptionContracts",
      filterByUpdatedAt: true,
    },
  },
  "2024-01": {
    fields: [
      {
        name: "Revision Id",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "revisionId", shopifyFieldKey: "revision_id" } },
      },
    ],
  },
};

export const ShopifySubscriptionContract = ({ apiVersion }: { apiVersion: ShopifyApiVersion }) => buildConnectionSpec(specs, apiVersion);
